import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import XIcon from '@mui/icons-material/X';
import { Button } from '@teuteuf-games/button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useDailySeed } from '../hooks/useDailySeed';
import { useGuessHistory } from '../hooks/useGuessHistory';
import { useRoundEmojis } from '../hooks/useRoundEmojis';
import { useTodaysCountry } from '../providers/TodaysCountryProvider';
import { trackEvent } from '../utils/analytics';
import { buildShareBody } from '../utils/dailyResult';
import { getPuzzleNumber } from '../utils/helpers';
import {
  buildChallengeUrl,
  canUseNativeShare,
  copyTextToClipboard,
  ShareChannel,
} from '../utils/share';

const DESTINATIONS: {
  channel: ShareChannel;
  label: string;
  icon: React.ReactNode;
  buildHref: (body: string, url: string) => string;
}[] = [
  {
    channel: 'whatsapp',
    label: 'WhatsApp',
    icon: <WhatsAppIcon fontSize="small" />,
    buildHref: (body, url) =>
      `https://wa.me/?text=${encodeURIComponent(`${body}\n${url}`)}`,
  },
  {
    channel: 'telegram',
    label: 'Telegram',
    icon: <TelegramIcon fontSize="small" />,
    // Telegram takes the link separately so it renders its own preview card.
    buildHref: (body, url) =>
      `https://t.me/share/url?url=${encodeURIComponent(
        url,
      )}&text=${encodeURIComponent(body)}`,
  },
  {
    channel: 'x',
    label: 'X',
    icon: <XIcon fontSize="small" />,
    buildHref: (body, url) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `${body}\n${url}`,
      )}`,
  },
];

export function ShareButton() {
  const { isArchive, dayString } = useTodaysCountry();
  const dailySeed = useDailySeed(isArchive);
  const [guessHistory] = useGuessHistory(isArchive);
  const textEmojis = useRoundEmojis(isArchive);
  const { t } = useTranslation();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { shareBody, puzzleNumber, scoreLabel } = useMemo(() => {
    const dailyGuesses = guessHistory[dailySeed] || [];
    const score = dailyGuesses.some(({ distance }) => distance === 0)
      ? dailyGuesses.length
      : 'DNF';

    const attempts = score === 'DNF' ? 'X' : dailyGuesses.length;

    return {
      shareBody: buildShareBody(
        { score, guesses: dailyGuesses, dayString },
        textEmojis,
      ),
      puzzleNumber: getPuzzleNumber(dayString),
      scoreLabel: String(attempts),
    };
  }, [guessHistory, dailySeed, dayString, textEmojis]);

  /** Params attached to every share event so results can be sliced in GA. */
  const eventParams = useMemo(
    () => ({
      content_type: 'flagle_result',
      item_id: `flagle-${puzzleNumber}`,
      puzzle_number: puzzleNumber,
      score: scoreLabel,
      is_archive: isArchive,
    }),
    [puzzleNumber, scoreLabel, isArchive],
  );

  const buildShareText = useCallback(
    (channel: ShareChannel) => `${shareBody}\n${buildChallengeUrl(channel)}`,
    [shareBody],
  );

  // Close the desktop panel on an outside click, matching the header menu.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isPanelOpen &&
        panelRef.current &&
        !event.composedPath().includes(panelRef.current)
      ) {
        setIsPanelOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isPanelOpen]);

  const copyAndToast = useCallback(
    async (text: string, channel: ShareChannel, successMessage: string) => {
      const copied = await copyTextToClipboard(text);
      if (copied) {
        trackEvent('share', { ...eventParams, method: channel });
        toast.success(successMessage, { autoClose: 2000 });
        return;
      }
      trackEvent('share_failed', {
        ...eventParams,
        method: channel,
        error: 'clipboard_unavailable',
      });
      toast.error(t('sharing.copyFailed'), { autoClose: 3000 });
    },
    [eventParams, t],
  );

  const handleNativeShare = useCallback(async () => {
    trackEvent('share_click', { ...eventParams, method: 'native' });
    try {
      await navigator.share({
        title: 'Flagle',
        text: buildShareText('native'),
      });
      trackEvent('share', { ...eventParams, method: 'native' });
    } catch (error) {
      // Dismissing the sheet rejects with AbortError — an intentional
      // cancellation, not a failure, and worth separating in reporting.
      const name = (error as Error)?.name;
      if (name === 'AbortError') {
        trackEvent('share_cancelled', { ...eventParams, method: 'native' });
        return;
      }
      trackEvent('share_failed', {
        ...eventParams,
        method: 'native',
        error: name ?? 'unknown',
      });
      await copyAndToast(buildShareText('clipboard'), 'clipboard', t('copy'));
    }
  }, [buildShareText, copyAndToast, eventParams, t]);

  const handleCopyResult = useCallback(async () => {
    trackEvent('share_click', { ...eventParams, method: 'clipboard' });
    await copyAndToast(buildShareText('clipboard'), 'clipboard', t('copy'));
    setIsPanelOpen(false);
  }, [buildShareText, copyAndToast, eventParams, t]);

  const handleCopyLink = useCallback(async () => {
    trackEvent('share_click', { ...eventParams, method: 'link' });
    await copyAndToast(
      buildChallengeUrl('link'),
      'link',
      t('sharing.linkCopied'),
    );
    setIsPanelOpen(false);
  }, [copyAndToast, eventParams, t]);

  const handleDestination = useCallback(
    (channel: ShareChannel, href: string) => {
      trackEvent('share_click', { ...eventParams, method: channel });
      trackEvent('share', { ...eventParams, method: channel });
      window.open(href, '_blank', 'noopener,noreferrer');
      setIsPanelOpen(false);
    },
    [eventParams],
  );

  const handlePrimaryClick = useCallback(() => {
    if (isMobile && canUseNativeShare()) {
      handleNativeShare();
      return;
    }
    if (isMobile) {
      handleCopyResult();
      return;
    }
    setIsPanelOpen((open) => {
      if (!open) {
        trackEvent('share_open_panel', { ...eventParams, method: 'panel' });
      }
      return !open;
    });
  }, [eventParams, handleCopyResult, handleNativeShare]);

  return (
    <div ref={panelRef} className="relative w-full">
      {isPanelOpen && (
        <div className="absolute z-[99] bottom-full left-0 mb-2 w-full min-w-max rounded-xl shadow-2xl bg-white dark:bg-slate-600 overflow-hidden text-left">
          <SharePanelItem
            icon={<ContentCopyIcon fontSize="small" />}
            label={t('sharing.copyResult')}
            onClick={handleCopyResult}
            isFirst
          />
          <div className="border-b border-gray-100 mx-2 dark:border-slate-500" />
          <SharePanelItem
            icon={<LinkIcon fontSize="small" />}
            label={t('sharing.copyLink')}
            onClick={handleCopyLink}
          />
          <div className="flex px-4 py-3 gap-2 justify-center items-center border-t border-gray-100 dark:border-slate-500">
            {DESTINATIONS.map(({ channel, label, icon, buildHref }) => (
              <button
                key={channel}
                type="button"
                title={label}
                aria-label={label}
                onClick={() =>
                  handleDestination(
                    channel,
                    buildHref(shareBody, buildChallengeUrl(channel)),
                  )
                }
                className="flex w-8 h-8 items-center justify-center rounded-full text-[#666666] dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors"
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      )}
      <Button
        buttonStyle={'secondary'}
        label={t('sharing.withFriends')}
        width="w-full"
        leadingIcon={<ShareIcon />}
        onClick={handlePrimaryClick}
      />
    </div>
  );
}

const SharePanelItem = ({
  icon,
  label,
  onClick,
  isFirst = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isFirst?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full px-4 py-2 ${
      isFirst ? 'pt-4' : ''
    } gap-3 text-sm items-center hover:bg-gray-200 dark:hover:bg-slate-500`}
  >
    <span className="text-[#666666] dark:text-gray-100">{icon}</span>
    <p className="whitespace-nowrap">{label}</p>
  </button>
);
